import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { redirectToAuthorize } from '../../utils/fetchService';
import createSocket from '../../utils/socketsHelpers';

import {
  fetchInitialDataForApproveTaxEntryPage,
  reviewTaxEntries,
  fetchTaxEntryForApprovalData,
  resetDataForApproveTaxEntryPage,
  resetTaxEntryForApprovalData,
  unlockTaxEntriesForApproval,
} from '../store/actions';
import {
  errorNotification,
} from '../../shared/store/notificationActions';
import TaxEntriesForApprovalGrid, { TaxEntryProps } from './taxEntriesForApprovalGrid.component';
import TaxEntryForApprovalDetails, { proposedJournalsPropTypes } from './taxEntryForApprovalDetails.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import { attachmentPropType } from '../viewTaxEntriesPage/propTypes';

class ApproveTaxEntriesPage extends PureComponent {
  static propTypes = {
    fetchInitialDataForApproveTaxEntryPage: PropTypes.func.isRequired,
    unlockTaxEntriesForApproval: PropTypes.func.isRequired,
    resetDataForApproveTaxEntryPage: PropTypes.func.isRequired,
    resetTaxEntryForApprovalData: PropTypes.func.isRequired,
    reviewTaxEntries: PropTypes.func.isRequired,
    fetchTaxEntryForApprovalData: PropTypes.func.isRequired,
    errorNotification: PropTypes.func.isRequired,
    taxEntriesForApprovalLoading: PropTypes.bool.isRequired,
    taxEntriesForApproval: PropTypes.arrayOf(TaxEntryProps).isRequired,
    taxEntryForApprovalData: PropTypes.shape({
      attachments: PropTypes.arrayOf(attachmentPropType).isRequired,
      taxEntryId: PropTypes.number,
    }).isRequired,
    proposedJournals: proposedJournalsPropTypes,
    lockIdForTaxEntriesBatch: PropTypes.string,
    isTaxEntriesReviewInProgress: PropTypes.bool,
    isTaxEntriesDeclineInProgress: PropTypes.bool,
    taxEntriesReviewProgress: PropTypes.number,
  };

  componentDidMount() {
    this.socket = createSocket();
    this.socket.on('disconnect', () => {
      // approveTaxEntries socket connection closed
      if (!this.socket.closedGracefully) {
        this.props.errorNotification(
          'Connection with the server has been lost! Please, reload the page and try again.',
          { timeOut: 15000 }
        );
      }
    });
    this.socket.on('error', error => {
      if (error === 'Unauthorized') {
        return redirectToAuthorize();
      }
      this.props.errorNotification(
        'Connection with the server failed. Please, reload the page and try again.'
      );
    });

    this.props.fetchInitialDataForApproveTaxEntryPage(this.socket);
    window.addEventListener('beforeunload', this.unlockTaxEntries);
  }

  componentWillUnmount() {
    this.socket.closedGracefully = true;
    this.socket.close();
    this.props.resetDataForApproveTaxEntryPage();

    window.removeEventListener('beforeunload', this.unlockTaxEntries);
  }

  unlockTaxEntries = () => {
    this.props.unlockTaxEntriesForApproval();
  };

  reviewTaxEntries = ({ taxEntryIds, reviewComment, type }) => {
    const { lockIdForTaxEntriesBatch: lockId } = this.props;

    this.props.reviewTaxEntries({ taxEntryIds, reviewComment, type, lockId, socket: this.socket });
    this.props.resetTaxEntryForApprovalData();
  };

  chooseTaxEntry = chosenTaxEntryId => {
    const chosenTaxEntry = this.props.taxEntriesForApproval.find(
      ({ taxEntryId }) => taxEntryId === chosenTaxEntryId
    );
    this.props.fetchTaxEntryForApprovalData(chosenTaxEntry);
  };

  render() {
    const {
      taxEntriesForApproval,
      taxEntriesForApprovalLoading,
      taxEntryForApprovalData,
      proposedJournals,
      isTaxEntriesReviewInProgress,
      isTaxEntriesDeclineInProgress,
      taxEntriesReviewProgress,
    } = this.props;

    const taxEntryDetails = taxEntriesForApproval.find(
      ({ taxEntryId }) => taxEntryId === taxEntryForApprovalData.taxEntryId
    );

    return (
      <div className="view-tax-entries-page">
        <NavigationPrompt
          show={isTaxEntriesReviewInProgress && isTaxEntriesDeclineInProgress}
          title="Are you sure you want to leave?"
          message="Processing of the remaining tax entries is going to be canceled"
        />
        <div className="page">
          <div className="container-fluid">
            <div className="row no-gutters">
              <div className="col">
                <TaxEntriesForApprovalGrid
                  taxEntriesForApproval={taxEntriesForApproval}
                  reviewTaxEntries={this.reviewTaxEntries}
                  chooseTaxEntry={this.chooseTaxEntry}
                  taxEntriesForApprovalLoading={taxEntriesForApprovalLoading}
                  isTaxEntriesReviewInProgress={isTaxEntriesReviewInProgress}
                  isTaxEntriesDeclineInProgress={isTaxEntriesDeclineInProgress}
                  taxEntriesReviewProgress={taxEntriesReviewProgress}
                />
              </div>
            </div>
            <div className="row no-gutters">
              <div className="col">
                {!taxEntryDetails ? null : (
                  <TaxEntryForApprovalDetails
                    attachments={taxEntryForApprovalData.attachments}
                    proposedJournals={proposedJournals}
                    taxEntry={taxEntryDetails}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps({ balanceSheetManagement }) {
  return {
    taxEntriesForApprovalLoading: balanceSheetManagement.shared.taxEntriesForApprovalLoading,
    taxEntriesForApproval: balanceSheetManagement.shared.taxEntriesForApproval,
    taxEntryForApprovalData: balanceSheetManagement.shared.taxEntryForApprovalData,
    proposedJournals: balanceSheetManagement.shared.proposedJournals,
    lockIdForTaxEntriesBatch: balanceSheetManagement.shared.lockIdForTaxEntriesBatch,
    isTaxEntriesReviewInProgress: balanceSheetManagement.shared.taxEntriesReviewInProgress,
    isTaxEntriesDeclineInProgress: balanceSheetManagement.shared.taxEntriesDeclineInProgress,
    taxEntriesReviewProgress: balanceSheetManagement.shared.taxEntriesReviewProgress,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForApproveTaxEntryPage,
    unlockTaxEntriesForApproval,
    reviewTaxEntries,
    fetchTaxEntryForApprovalData,
    resetDataForApproveTaxEntryPage,
    resetTaxEntryForApprovalData,
    errorNotification,
  }
)(ApproveTaxEntriesPage);
